import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Eye, EyeOff, CalendarIcon, Users as UsersIcon, Compass, Trophy, Heart, Globe, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { PROFESSIONS, formatCustomProfession } from "@/lib/constants";
import GoogleButton, { AuthDivider } from "@/components/auth/GoogleButton";

const ROLES = [
  { value: "athlete", label: "Athlete", icon: Trophy, desc: "Play & be discovered" },
  { value: "scout", label: "Scout", icon: Compass, desc: "Find talent" },
  { value: "coach", label: "Coach", icon: UsersIcon, desc: "Build your squad" },
  { value: "explorer", label: "Explorer", icon: Globe, desc: "Join & browse" },
  { value: "professional", label: "Professional", icon: Briefcase, desc: "Physio, doctor & more" },
  { value: "parent", label: "Parent", icon: Heart, desc: "Follow your child" },
];

const POSITIONS = [
  "Goalkeeper",
  "Right Back",
  "Left Back",
  "Centre Back",
  "Defender",
  "Defensive Midfielder",
  "Central Midfielder",
  "Attacking Midfielder",
  "Midfielder",
  "Right Winger",
  "Left Winger",
  "Winger",
  "Second Striker",
  "Striker",
  "Forward",
  "Other",
];

const UEFA_LEVELS = ["UEFA C", "UEFA B", "UEFA A", "UEFA Pro", "UEFA Elite Youth A", "Other (specify)", "I'll add this later"];
const COACHING_ROLES = ["Head Coach", "Assistant Coach", "Goalkeeper Coach", "Fitness Coach", "Youth Coach", "Analyst", "Other"];
const SCOUT_FOCUS = ["Youth Players", "Senior Players", "Goalkeepers", "Defenders", "Midfielders", "Forwards", "General"];

function DateField({ value, onChange, placeholder = "Pick a date", testId }) {
  const [open, setOpen] = useState(false);
  const selectedDate = value ? new Date(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          className={`mt-1.5 w-full justify-start border-[#E6E6E6] font-normal ${value ? "text-zinc-900" : "text-zinc-400"}`}
          data-testid={testId}
        >
          <CalendarIcon size={14} className="mr-2 text-zinc-400" />
          {value ? new Date(value).toLocaleDateString() : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => { if (d) { onChange(d.toISOString().slice(0, 10)); setOpen(false); } }}
          captionLayout="dropdown-buttons"
          fromYear={1950}
          toYear={new Date().getFullYear()}
          defaultMonth={selectedDate || new Date(2008, 0)}
          classNames={{
            caption_label: "hidden",
            caption_dropdowns: "flex gap-2 items-center justify-center",
            dropdown: "appearance-none bg-white border border-[#E6E6E6] rounded-md px-2 py-1 text-sm cursor-pointer hover:border-[#074572] focus:outline-none focus:border-[#074572]",
            dropdown_month: "relative",
            dropdown_year: "relative",
            vhidden: "sr-only",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export default function SignupPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("athlete");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Role-specific
  const [dob, setDob] = useState("");
  const [position, setPosition] = useState("");
  const [club, setClub] = useState("");
  const [coachingLicense, setCoachingLicense] = useState("");
  const [customLicense, setCustomLicense] = useState("");
  const [coachingRole, setCoachingRole] = useState("");
  const [scoutingRegion, setScoutingRegion] = useState("");
  const [scoutingFocus, setScoutingFocus] = useState("");
  const [childName, setChildName] = useState("");
  const [childDob, setChildDob] = useState("");
  const [childClub, setChildClub] = useState("");
  const [profession, setProfession] = useState("");
  const [customProfession, setCustomProfession] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const customLicenseNeeded = coachingLicense === "Other (specify)";
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    // Validate role-specific required fields
    if (role === "athlete" && (!dob || !position || !club.trim())) {
      setError("Athletes must provide date of birth, position and academy/club.");
      return;
    }
    if (role === "coach" && customLicenseNeeded && !customLicense.trim()) {
      setError("Please enter your diploma/license, or choose \"I'll add this later\".");
      return;
    }
    if (role === "scout" && (!club.trim() || !scoutingRegion.trim() || !scoutingFocus)) {
      setError("Scouts must provide organization, region and main focus.");
      return;
    }
    if (role === "parent" && (!childName.trim() || !childDob || !childClub.trim())) {
      setError("Parents must provide child name, date of birth and academy/club.");
      return;
    }
    if (role === "professional") {
      if (!profession) { setError("Please select your profession."); return; }
      if (profession === "Other" && !customProfession.trim()) { setError("Please enter your profession."); return; }
    }

    const extras = {};
    if (role === "athlete") Object.assign(extras, { date_of_birth: dob, position, club });
    if (role === "coach") {
      let lic = coachingLicense;
      if (coachingLicense === "I'll add this later" || !coachingLicense) lic = "";
      else if (coachingLicense === "Other (specify)") lic = customLicense.trim();
      Object.assign(extras, { coaching_license: lic, club: club || undefined, coaching_role: coachingRole || undefined });
    }
    if (role === "scout") Object.assign(extras, { club, scouting_region: scoutingRegion, scouting_focus: scoutingFocus });
    if (role === "parent") Object.assign(extras, { child_name: childName, child_date_of_birth: childDob, child_club: childClub });
    if (role === "explorer" && club.trim()) Object.assign(extras, { club });
    if (role === "professional") Object.assign(extras, { profession: profession === "Other" ? customProfession.trim() : profession, club: club || undefined });

    setLoading(true);
    try {
      await register(email, password, name, role, extras);
      toast.success("Account created! Welcome to SportsUnity.");
      navigate("/feed");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="signup-page">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#074572] relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-[#074572] to-[#053a60]" />
        <div className="relative z-10 p-12 text-white max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-11 h-11 rounded-md bg-white/20 flex items-center justify-center p-1">
              <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>SportsUnity</span>
          </div>
          <h2 className="text-4xl font-medium tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
            Join the football community
          </h2>
          <p className="text-white/70 leading-relaxed">
            Athletes, scouts, coaches, and parents — connect, develop and discover talent in one place.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-md bg-white border border-[#E6E6E6] flex items-center justify-center p-1">
              <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
            </div>
            <span className="text-lg font-semibold text-[#074572]" style={{ fontFamily: 'Outfit' }}>SportsUnity</span>
          </div>

          <h1 className="text-2xl font-medium text-zinc-900 mb-1" style={{ fontFamily: 'Outfit' }}>Create account</h1>
          <p className="text-sm text-zinc-500 mb-6">Tell us a bit about yourself</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600" data-testid="signup-error">
              {error}
            </div>
          )}

          <GoogleButton label="Sign up with Google" testId="signup-google-button" />
          <AuthDivider />

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Role picker - pills */}
            <div>
              <Label className="text-zinc-700 text-sm">I am a</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="signup-role-grid">
                {ROLES.map(r => {
                  const Icon = r.icon;
                  const active = role === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setRole(r.value)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-md border text-left transition-all ${
                        active
                          ? "border-[#074572] bg-[#074572]/5 ring-1 ring-[#074572]/30"
                          : "border-[#E6E6E6] hover:border-[#074572]/40 hover:bg-zinc-50"
                      }`}
                      data-testid={`signup-role-${r.value}`}
                    >
                      <Icon size={16} className={active ? "text-[#074572]" : "text-zinc-500"} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${active ? "text-[#074572]" : "text-zinc-900"}`}>{r.label}</p>
                        <p className="text-[10px] text-zinc-500 truncate">{r.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="name" className="text-zinc-700 text-sm">Full Name</Label>
              <Input id="name" placeholder="e.g. Giorgos Papadopoulos" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-name-input" required />
            </div>
            <div>
              <Label htmlFor="email" className="text-zinc-700 text-sm">Email</Label>
              <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-email-input" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-700 text-sm">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPw ? "text" : "password"} placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="border-[#E6E6E6] pr-10" data-testid="signup-password-input" required />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Role-specific fields */}
            {role === "athlete" && (
              <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="athlete-fields">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Athlete details</p>
                <div>
                  <Label className="text-zinc-700 text-sm">Date of Birth</Label>
                  <DateField value={dob} onChange={setDob} placeholder="Select your date of birth" testId="signup-dob" />
                </div>
                <div>
                  <Label className="text-zinc-700 text-sm">Position</Label>
                  <Select value={position} onValueChange={setPosition}>
                    <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="signup-position-select"><SelectValue placeholder="Select position" /></SelectTrigger>
                    <SelectContent>{POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-700 text-sm">Academy / Club</Label>
                  <Input placeholder="e.g. PAOK U19, Makedonikos FC" value={club} onChange={(e) => setClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-club-input" />
                </div>
              </div>
            )}

            {role === "coach" && (
              <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="coach-fields">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Coach details <span className="text-zinc-300 normal-case">(optional)</span></p>
                <div>
                  <Label className="text-zinc-700 text-sm">Diploma / License Level</Label>
                  <Select value={coachingLicense} onValueChange={setCoachingLicense}>
                    <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="signup-license-select"><SelectValue placeholder="Select license (or add later)" /></SelectTrigger>
                    <SelectContent>{UEFA_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {coachingLicense === "Other (specify)" && (
                  <div>
                    <Label className="text-zinc-700 text-sm">Your diploma / license</Label>
                    <Input placeholder="e.g. National C License" value={customLicense} onChange={(e) => setCustomLicense(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-license-other" />
                  </div>
                )}
                <div>
                  <Label className="text-zinc-700 text-sm">Current Team / Club</Label>
                  <Input placeholder="e.g. Olympiacos FC" value={club} onChange={(e) => setClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-coach-club" />
                </div>
                <div>
                  <Label className="text-zinc-700 text-sm">Coaching Role</Label>
                  <Select value={coachingRole} onValueChange={setCoachingRole}>
                    <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="signup-coaching-role-select"><SelectValue placeholder="Select coaching role" /></SelectTrigger>
                    <SelectContent>{COACHING_ROLES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <p className="text-[11px] text-zinc-500 bg-zinc-50 border border-[#E6E6E6] rounded p-2 leading-snug">
                  You can skip these now and add your diploma — including uploading the certificate for verification — later from Settings.
                </p>
              </div>
            )}

            {role === "scout" && (
              <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="scout-fields">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Scout details</p>
                <div>
                  <Label className="text-zinc-700 text-sm">Organization / Club</Label>
                  <Input placeholder="e.g. Chelsea FC, Independent" value={club} onChange={(e) => setClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-scout-club" />
                </div>
                <div>
                  <Label className="text-zinc-700 text-sm">Scouting Region</Label>
                  <Input placeholder="e.g. Greece, Thessaloniki, Europe" value={scoutingRegion} onChange={(e) => setScoutingRegion(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-region-input" />
                </div>
                <div>
                  <Label className="text-zinc-700 text-sm">Main Focus</Label>
                  <Select value={scoutingFocus} onValueChange={setScoutingFocus}>
                    <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="signup-focus-select"><SelectValue placeholder="Select focus" /></SelectTrigger>
                    <SelectContent>{SCOUT_FOCUS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {role === "professional" && (
              <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="professional-fields">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Professional details</p>
                <div>
                  <Label className="text-zinc-700 text-sm">Profession</Label>
                  <Select value={profession} onValueChange={setProfession}>
                    <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="signup-profession-select"><SelectValue placeholder="Select profession" /></SelectTrigger>
                    <SelectContent>{PROFESSIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {profession === "Other" && (
                  <div>
                    <Label className="text-zinc-700 text-sm">Your profession</Label>
                    <Input placeholder="e.g. Kinesiologist" value={customProfession} onChange={(e) => setCustomProfession(formatCustomProfession(e.target.value))} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-profession-other" />
                  </div>
                )}
                <div>
                  <Label className="text-zinc-700 text-sm">Organization / Club (optional)</Label>
                  <Input placeholder="e.g. AEK Athens" value={club} onChange={(e) => setClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-professional-club" />
                </div>
              </div>
            )}

            {role === "explorer" && (
              <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="explorer-fields">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Explorer</p>
                <div>
                  <Label className="text-zinc-700 text-sm">Organization / Club (optional)</Label>
                  <Input placeholder="e.g. Independent" value={club} onChange={(e) => setClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-explorer-club" />
                </div>
                <p className="text-[11px] text-zinc-500 bg-zinc-50 border border-[#E6E6E6] rounded p-2 leading-snug">
                  Explorers can browse the network, discover talent and connect — the same access as scouts and coaches.
                </p>
              </div>
            )}

            {role === "parent" && (
              <div className="border-t border-[#E6E6E6] pt-4 space-y-3" data-testid="parent-fields">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Your child&apos;s details</p>
                <div>
                  <Label className="text-zinc-700 text-sm">Child&apos;s Full Name</Label>
                  <Input placeholder="e.g. Nikos Papadopoulos" value={childName} onChange={(e) => setChildName(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-child-name" />
                </div>
                <div>
                  <Label className="text-zinc-700 text-sm">Child&apos;s Date of Birth</Label>
                  <DateField value={childDob} onChange={setChildDob} placeholder="Select child's date of birth" testId="signup-child-dob" />
                </div>
                <div>
                  <Label className="text-zinc-700 text-sm">Child&apos;s Academy / Club</Label>
                  <Input placeholder="e.g. PAOK U15" value={childClub} onChange={(e) => setChildClub(e.target.value)} className="mt-1.5 border-[#E6E6E6]" data-testid="signup-child-club" />
                </div>
                <p className="text-[11px] text-zinc-500 bg-zinc-50 border border-[#E6E6E6] rounded p-2 leading-snug">
                  Parent accounts are read-only — you can browse, view profiles and message, but you cannot post or be discovered by scouts. You can link your child&apos;s athlete account from Settings later.
                </p>
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full bg-[#F85037] hover:bg-[#e04530] text-white font-medium h-10" data-testid="signup-submit-button">
              {loading ? "Creating account..." : "Create Account"}
            </Button>
          </form>

          <p className="text-sm text-zinc-500 text-center mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#074572] font-medium hover:underline" data-testid="signup-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
